import { useEffect, useMemo, useState } from 'react';

import { I18nextProvider } from 'react-i18next';

import {
  AnalyticsProvider,
  AnalyticsProviderUser,
} from '@storefront-core/analytics-web';

import { LayoutProvider } from '@/contexts/LayoutContext';
import { SearchContextProvider } from '@/contexts/Search.context';
import { getUserInfo } from '@/utils/analytics.util';
import { detectDeviceType } from '@/utils/getDeviceType';
import { detectReactNativeWebView } from '@/utils/platform.util';

import i18n from '../configs/i18n/i18n';

const analyticsConfig = {
  segmentWebWriteKey: process.env.NEXT_PUBLIC_SEGMENT_WEB_WRITE_KEY,
  segmentAppWriteKey: process.env.NEXT_PUBLIC_SEGMENT_APP_WRITE_KEY,
  gtmId: process.env.NEXT_PUBLIC_GTM_ID,
  debug: process.env.NODE_ENV === 'development',
  flashAppId: process.env.NEXT_PUBLIC_FLASH_APP_ID,
  flashEnabled: process.env.NEXT_PUBLIC_FLASH_ENABLED === 'true',
};

export function AppProviders({
  children,
  locale,
  userAgent,
  host,
}: {
  children: React.ReactNode;
  locale: 'en' | 'th';
  userAgent: string;
  host: string;
}) {
  const deviceType = useMemo(() => detectDeviceType(userAgent), [userAgent]);
  const isInWebView = useMemo(
    () => detectReactNativeWebView(userAgent, host),
    [userAgent, host]
  );

  const [userInfo] = useState<AnalyticsProviderUser | null>(() =>
    getUserInfo()
  );

  // Update language when route changes
  useEffect(() => {
    if (locale === 'en' || locale === 'th') {
      i18n.changeLanguage(locale);
    }
  }, [locale]);

  const isAuthenticated = !!(userInfo?.id && userInfo.id.trim() !== '');

  return (
    <I18nextProvider i18n={i18n}>
      {/* <SessionRefreshedSync /> */}
      <AnalyticsProvider
        config={analyticsConfig}
        user={isAuthenticated ? userInfo : null}
        isAuthenticated={isAuthenticated}
      >
        <LayoutProvider deviceType={deviceType} isInWebView={isInWebView}>
          <SearchContextProvider>{children}</SearchContextProvider>
        </LayoutProvider>
      </AnalyticsProvider>
    </I18nextProvider>
  );
}
